import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { campaignsEndpoints } from '@/api/endpoints';
import { selectAccountSlug } from '@/redux/session/selectors';
import {
  Campaign,
  CampaignSender,
  CampaignSubscriber,
  CampaignTransition,
  NewCampaignSender,
  NewCampaignSubscriber,
  SubscriberContext,
} from '@/shared/types/campaigns';
import { NAME_SUFFIX_BUDGET, resolveAvailableName, truncateToServerLimit } from '@/util/checkNames';
import { LENGTH_XS } from '@/util/validation/constants';

import { API as campaignsApi } from '../api';
import { useCampaigns } from './useCampaigns';
import { API as sendersApi, useCampaignSenders } from './useCampaignSenders';
import { fetchAllCampaignSubscribers, useCampaignSubscribers } from './useCampaignSubscribers';

/**
 * Truncate the source name and ask the orchestrator for the next available
 * variant (e.g. `Foo` → `Foo (1)`). Exported for unit testing.
 */
export const resolveDuplicateName = async (
  sourceName: string,
  accountSlug: string
): Promise<string> => {
  const baseName = truncateToServerLimit(sourceName, LENGTH_XS - NAME_SUFFIX_BUDGET);
  return resolveAvailableName(campaignsEndpoints.checkName(baseName), baseName, { accountSlug });
};

const toNewSenderWithoutTransitions = (sender: CampaignSender): NewCampaignSender => ({
  integration_id: sender.integration_id,
  template: sender.template,
  metadata: sender.metadata,
});

// NBA seeds runtime-only keys (e.g. the phone-keyed `engagement_brief`) into a
// subscriber's `context` at send time. These must not be carried into a
// duplicated campaign, or a fresh contact inherits the source campaign's stale
// reasoning. Mirrors the orchestrator's NBA_CONTEXT_KEYS (orchestrator PR #2100
// / MOV-17696); there's no shared package, so this list is a manual parallel —
// keep the two in sync.
const NBA_RUNTIME_CONTEXT_KEYS = ['engagement_brief'] as const;

// Drop NBA runtime keys while preserving genuine custom vars (e.g. promo_code).
const stripNbaRuntimeContext = (
  context: SubscriberContext | undefined
): SubscriberContext | undefined => {
  if (!context) {
    return context;
  }
  const cleaned = { ...context };
  for (const key of NBA_RUNTIME_CONTEXT_KEYS) {
    delete cleaned[key];
  }
  return cleaned;
};

// Keys the orchestrator owns on `campaign_subscribers.metadata`: a manual stop's
// `skip_fallback` and the first-message gate markers. Copying them makes every
// duplicate start stopped or already contacted. The orchestrator also drops
// them on upload (orchestrator PR #2425); keep the two lists in sync.
const SYSTEM_METADATA_KEYS = [
  'skip_fallback',
  'first_message_sent_at',
  'first_message_defer_count',
  'first_message_terminal_reason',
] as const;

const stripSystemMetadata = (
  metadata: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!metadata) {
    return metadata;
  }
  const cleaned = { ...metadata };
  for (const key of SYSTEM_METADATA_KEYS) {
    delete cleaned[key];
  }
  return cleaned;
};

// Exported for unit testing.
export const toNewSubscriber = (subscriber: CampaignSubscriber): NewCampaignSubscriber => ({
  // Omit phone/email when null — the orchestrator schema rejects nulls but
  // accepts the fields being absent. The source guarantees at least one is set.
  ...(subscriber.phone ? { phone: subscriber.phone } : {}),
  ...(subscriber.email ? { email: subscriber.email } : {}),
  context: stripNbaRuntimeContext(subscriber.context),
  metadata: stripSystemMetadata(subscriber.metadata),
});

const remapTransitions = (
  transitions: CampaignTransition[] | undefined,
  idMap: Map<string, string>
): CampaignTransition[] | undefined => {
  if (!transitions?.length) {
    return undefined;
  }
  const remapped = transitions
    .map((transition) => {
      if (!transition.sender_id) {
        return transition;
      }
      const newSenderId = idMap.get(transition.sender_id);
      // Drop transitions whose target sender wasn't created on the duplicate.
      return newSenderId ? { ...transition, sender_id: newSenderId } : null;
    })
    .filter((t): t is CampaignTransition => t !== null);
  return remapped.length > 0 ? remapped : undefined;
};

export const stripTags = (
  context: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => {
  if (!context) {
    return context;
  }
  const cleaned = { ...context };
  delete cleaned.tags;
  return cleaned;
};

// `nba_config` must be paired with `is_agentic: true` on the same payload —
// otherwise the orchestrator rejects it. See useUnifiedSetupForm.ts:608.
// Pass `nba_config` through as-is: `channel_follow_up_defaults` is required by
// the schema when `integration_mappings.whatsapp` is set, so we can't filter.
// `brain_id` is server-derived from brain_parent_id + brain_version and is
// omitted by the setup-wizard's create payload.
const buildDuplicatePayload = (source: Campaign, name: string): Partial<Campaign> => {
  const payload: Partial<Campaign> = {
    name,
    type: source.type,
    brain_parent_id: source.brain_parent_id,
    brain_version: source.brain_version,
    subscribers_context: stripTags(source.subscribers_context),
    metadata: source.metadata,
  };
  if (source.nba_config) {
    payload.nba_config = source.nba_config;
    payload.is_agentic = source.is_agentic ?? true;
  }
  return payload;
};

export const useDuplicateCampaign = () => {
  const { t } = useTranslation();
  const { createCampaignAsync, verifySubscribersAsync } = useCampaigns();
  const { createCampaignSenderAsync, updateCampaignSenderAsync } = useCampaignSenders();
  const { bulkUpsertSubscribersAsync } = useCampaignSubscribers();
  const slug = useSelector(selectAccountSlug);
  const [isDuplicating, setIsDuplicating] = useState(false);

  const duplicateCampaign = async (
    campaign: Campaign,
    onView?: (newCampaign: Campaign) => void
  ): Promise<Campaign> => {
    if (!slug) {
      throw new Error('Cannot duplicate a campaign without an account');
    }
    // Pin the account so the name check and the create cannot follow an account switch.
    const accountSlug = slug;

    setIsDuplicating(true);
    const toastId = toast.info(t('campaigns.duplicating'), {
      description: t('campaigns.duplicating_description'),
      duration: Infinity,
    });
    try {
      // Refetch the full source. The campaigns list response is a slim Campaign
      // shape that omits `nba_config` and `is_agentic` (and likely other fields),
      // so we can't trust the row data for the create payload.
      const source = await campaignsApi.getCampaign(campaign.campaign_id);

      // Kick off source-data fetches in parallel with campaign creation. The
      // dedicated endpoints return full senders/subscribers; the campaigns list
      // response embeds slimmer shapes that drop template metadata and pagination.
      const sourceSendersPromise = sendersApi.listCampaignSenders(source.campaign_id);
      const sourceSubscribersPromise = fetchAllCampaignSubscribers(source.campaign_id, {
        limit: 500,
      });

      const name = await resolveDuplicateName(source.name, accountSlug);
      const newCampaign = await createCampaignAsync({
        campaign: buildDuplicatePayload(source, name),
        accountSlug,
      });

      const viewAction = onView
        ? { label: t('common.view'), onClick: () => onView(newCampaign) }
        : undefined;

      // Two passes because CampaignTransition.sender_id is a foreign key into
      // the campaign's other senders. Pass 1 creates each sender without
      // transitions and records the source→new sender_id mapping. Pass 2
      // PATCHes each sender's transitions with remapped sender_id refs. Within
      // each pass operations are independent, so they run in parallel.
      const copySenders = async () => {
        const { campaign_senders: sources } = await sourceSendersPromise;
        if (sources.length === 0) {
          return;
        }

        const created = await Promise.all(
          sources.map((sender) =>
            createCampaignSenderAsync({
              campaignId: newCampaign.campaign_id,
              sender: toNewSenderWithoutTransitions(sender),
            }).then((newSender) => [sender.sender_id, newSender.sender_id] as const)
          )
        );
        const idMap = new Map<string, string>(created);

        await Promise.all(
          sources.flatMap((sender) => {
            const transitions = remapTransitions(sender.transitions, idMap);
            if (!transitions) {
              return [];
            }
            return [
              updateCampaignSenderAsync({
                sender_id: idMap.get(sender.sender_id)!,
                campaign_id: newCampaign.campaign_id,
                transitions,
              }),
            ];
          })
        );
      };

      // Subscriber rows live in a separate store. Strip server-managed fields,
      // verify (Tier A+B) against the new campaign, then bulk-upsert.
      // All-or-nothing on verify: a clean Tier B run guarantees the upsert
      // succeeds; a non-empty error list means we leave the new draft with no
      // subscribers and let the user re-upload manually.
      const copySubscribers = async () => {
        const sourceSubscribers = await sourceSubscribersPromise;
        if (sourceSubscribers.length === 0) {
          return;
        }

        const subscribers = sourceSubscribers.map(toNewSubscriber);

        const verifyErrors = await verifySubscribersAsync({
          campaign_id: newCampaign.campaign_id,
          subscribers,
        });
        if (verifyErrors.length > 0) {
          throw new Error('subscriber verify rejected rows');
        }

        await bulkUpsertSubscribersAsync({
          campaign_id: newCampaign.campaign_id,
          subscribers,
          replace: false,
        });
      };

      try {
        await Promise.all([copySenders(), copySubscribers()]);
        toast.success(t('campaigns.duplicate_success', { 0: newCampaign.name }), {
          id: toastId,
          action: viewAction,
        });
      } catch {
        toast.warning(t('campaigns.duplicate_partial_error'), { id: toastId, action: viewAction });
      }

      return newCampaign;
    } catch (error) {
      toast.dismiss(toastId);
      throw error;
    } finally {
      setIsDuplicating(false);
    }
  };

  return { duplicateCampaign, isDuplicating };
};
